import React from "react";
import ActionButton, { useActionStatus } from "./ActionButton";
import jsPDF from "jspdf";
import { FileDown } from "lucide-react";
import { expertOptions } from "../constants/experts";
import { getExpertPalette } from "../utils/expertStyles";
import { DebateSource, visibleSources } from "../utils/sources";

/** Human-friendly label for an expert key, e.g. "nutrition" → "Dietician" */
function expertLabel(key: string): string {
  return (
    expertOptions.find((e) => e.value === key)?.label ??
    key.charAt(0).toUpperCase() + key.slice(1)
  );
}

interface DebateStep {
  expert: string;
  step: string; // "initial" | "feedback" | "chaos" | "revision"
  round?: number;
  answer?: string;
  feedback?: string;
  revised?: string;
  chaos?: string;
  done?: boolean;
}

interface ChaosStep {
  round: number;
  chaos: string;
  model?: string;
}

export interface DebatePDFButtonProps {
  debateTopic: string;
  councilProgress: DebateStep[];
  chaosProgress: ChaosStep[];
  councilFinal: string | null;
  selectedExperts: string[];
  debateMode?: "single" | "multi";
  sources?: DebateSource[];
}

// ─── PDF layout constants ─────────────────────────────────────────────────────
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;

// Text x-position inside a card (left bar 3.5 + gap 3.5 = 7mm from margin)
const CARD_TEXT_X = MARGIN + 7;
// Right edge of text inside a card (4mm padding from right margin)
const CARD_TEXT_RIGHT = PAGE_W - MARGIN - 4;
// Safety buffer so splitTextToSize rounding never exceeds the card border
const WRAP_SAFETY = 4;
// Usable text wrap width inside expert/chaos cards
const CARD_TEXT_W = CARD_TEXT_RIGHT - CARD_TEXT_X - WRAP_SAFETY; // ~159mm

// ─── Font size scale ──────────────────────────────────────────────────────────
const FS_BODY = 12; // main argument / answer text
const FS_CARD_LABEL = 10; // step label (top-right of card, italic)
const FS_CARD_HEADER = 13; // expert name in card header
const FS_SECTION = 13; // section heading bar text
const FS_CHAOS_HEADER = 13; // chaos card header
const FS_FINAL_HEADER = 13; // facilitator card header
const FS_TOC = 11; // table of contents items
const FS_COVER_TITLE = 28; // cover page big title
const FS_COVER_SUB = 14; // cover page subtitle / date
const FS_STAT_VALUE = 20; // stat box big number
const FS_STAT_LABEL = 11; // stat box label
const FS_CHIP = 10; // expert chip label
const FS_FOOTER = 10; // page footer

// Line height for body text (mm) — scales with FS_BODY (12pt ≈ 4.23mm, use 6.5mm for comfort)
const BODY_LINE_H = 6.5;
// Card header block height (mm) — must fit header text + divider + gap before body
const CARD_HEADER_H = 22;

// ─── Markdown → plain-text (strip syntax for PDF) ─────────────────────────────

/** Replace common Unicode characters that Helvetica cannot measure / render */
function sanitizeForPDF(text: string): string {
  return (
    text
      // Emojis (broad ranges)
      .replace(
        /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu,
        "",
      )
      // Em / en dashes
      .replace(/[\u2013\u2014]/g, "--")
      // Smart quotes
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      // Bullets / middle dots
      .replace(/[\u2022\u00B7\u2023\u25E6\u2043]/g, "-")
      // Ellipsis
      .replace(/\u2026/g, "...")
      // Non-breaking space
      .replace(/\u00A0/g, " ")
      // Any remaining non-Latin-1 characters that Helvetica can't handle
      // eslint-disable-next-line no-control-regex -- PDF Helvetica supports only Latin-1 code points.
      .replace(/[^\x00-\xFF]/g, "")
  );
}

function mdToPlain(md: string): string {
  const cleaned = md
    .replace(/```[\s\S]*?```/g, (block) =>
      block
        .replace(/```[a-z]*/i, "")
        .replace(/```/, "")
        .trim(),
    )
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/!\[.*?\]\(.*?\)/g, "")
    .replace(/\[(.+?)\]\(.*?\)/g, "$1")
    .replace(/^[-*+]\s+/gm, "- ")
    .replace(/^\d+\.\s+/gm, (m) => m)
    .replace(/^>\s+/gm, "  ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return sanitizeForPDF(cleaned);
}

// ─── Markdown table parser ────────────────────────────────────────────────────
interface ParsedTable {
  headers: string[];
  rows: string[][];
}

/** Splits markdown into segments of plain text and tables */
type ContentSegment =
  | { type: "text"; text: string }
  | { type: "table"; table: ParsedTable };

function parseMarkdownContent(md: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  const lines = md.split("\n");
  let i = 0;
  let textBuffer: string[] = [];

  function flushText() {
    if (textBuffer.length > 0) {
      const joined = textBuffer.join("\n").trim();
      if (joined) segments.push({ type: "text", text: mdToPlain(joined) });
      textBuffer = [];
    }
  }

  while (i < lines.length) {
    const line = lines[i];
    // Detect table: a line with | that is followed by a separator line (|---|...)
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      /^\s*\|?\s*[-:]+[-|:\s]+$/.test(lines[i + 1])
    ) {
      flushText();
      // Parse header
      const headerCells = line
        .split("|")
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
      i += 2; // skip header + separator

      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes("|")) {
        const cells = lines[i]
          .split("|")
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        if (cells.length === 0) break;
        rows.push(cells);
        i++;
      }
      segments.push({ type: "table", table: { headers: headerCells, rows } });
    } else {
      textBuffer.push(line);
      i++;
    }
  }
  flushText();
  return segments;
}

/** Strip bold/italic markdown from a cell value for PDF */
function stripInlineMarkdown(s: string): string {
  return sanitizeForPDF(
    s
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/\*(.+?)\*/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/\[(.+?)\]\(.*?\)/g, "$1")
      .trim(),
  );
}

// ─── Table rendering for jsPDF ────────────────────────────────────────────────
const TABLE_FONT_SIZE = 9;
const TABLE_HEADER_FONT_SIZE = 9;
const TABLE_LINE_H = 5;
const TABLE_CELL_PAD = 3;
const TABLE_HEADER_BG: [number, number, number] = [241, 245, 249]; // slate-100
const TABLE_BORDER_COLOR: [number, number, number] = [203, 213, 225]; // slate-300
const TABLE_ALT_ROW_BG: [number, number, number] = [248, 250, 252]; // slate-50
const TABLE_HEADER_TEXT: [number, number, number] = [30, 41, 59]; // slate-800

/**
 * Renders a parsed markdown table into the PDF at the given Y position.
 * Returns the new Y cursor after the table.
 */
function drawTable(
  doc: jsPDF,
  table: ParsedTable,
  x: number,
  maxWidth: number,
  yPos: number,
  newPage: () => number,
): number {
  const numCols = table.headers.length;
  if (numCols === 0) return yPos;

  const colW = maxWidth / numCols;
  const PAGE_BOTTOM = PAGE_H - MARGIN - 12;

  // Compute wrapped lines for each cell
  function wrapCell(text: string): string[] {
    const clean = stripInlineMarkdown(text);
    doc.setFontSize(TABLE_FONT_SIZE);
    return doc.splitTextToSize(clean, colW - TABLE_CELL_PAD * 2) as string[];
  }

  function wrapHeaderCell(text: string): string[] {
    const clean = stripInlineMarkdown(text);
    doc.setFontSize(TABLE_HEADER_FONT_SIZE);
    doc.setFont("helvetica", "bold");
    return doc.splitTextToSize(clean, colW - TABLE_CELL_PAD * 2) as string[];
  }

  // Function to draw one row
  function drawRow(
    cells: string[],
    y: number,
    isHeader: boolean,
    isAlt: boolean,
  ): { newY: number; overflow: boolean } {
    // Compute row height
    const wrappedCells = cells.map((cell) =>
      isHeader ? wrapHeaderCell(cell) : wrapCell(cell),
    );
    const maxLines = Math.max(1, ...wrappedCells.map((w) => w.length));
    const rowH = maxLines * TABLE_LINE_H + TABLE_CELL_PAD * 2;

    if (y + rowH > PAGE_BOTTOM) {
      return { newY: y, overflow: true };
    }

    // Row background
    if (isHeader) {
      doc.setFillColor(...TABLE_HEADER_BG);
      doc.rect(x, y, maxWidth, rowH, "F");
    } else if (isAlt) {
      doc.setFillColor(...TABLE_ALT_ROW_BG);
      doc.rect(x, y, maxWidth, rowH, "F");
    }

    // Cell borders and text
    doc.setDrawColor(...TABLE_BORDER_COLOR);
    doc.setLineWidth(0.2);

    for (let col = 0; col < numCols; col++) {
      const cx = x + col * colW;
      // Cell border
      doc.rect(cx, y, colW, rowH, "S");

      // Cell text
      const lines = wrappedCells[col] || [];
      if (isHeader) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(TABLE_HEADER_FONT_SIZE);
        doc.setTextColor(...TABLE_HEADER_TEXT);
      } else {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(TABLE_FONT_SIZE);
        doc.setTextColor(30, 41, 59);
      }

      let ty = y + TABLE_CELL_PAD + TABLE_LINE_H * 0.7;
      for (const line of lines) {
        doc.text(line, cx + TABLE_CELL_PAD, ty, {
          maxWidth: colW - TABLE_CELL_PAD * 2,
        });
        ty += TABLE_LINE_H;
      }
    }

    return { newY: y + rowH, overflow: false };
  }

  let y = yPos;

  // Need room for at least the header row
  if (y + TABLE_LINE_H + TABLE_CELL_PAD * 2 > PAGE_BOTTOM) {
    y = newPage();
  }

  // Draw header
  const paddedHeaders = [...table.headers];
  while (paddedHeaders.length < numCols) paddedHeaders.push("");
  let result = drawRow(paddedHeaders, y, true, false);
  if (result.overflow) {
    y = newPage();
    result = drawRow(paddedHeaders, y, true, false);
  }
  y = result.newY;

  // Draw data rows
  for (let r = 0; r < table.rows.length; r++) {
    const paddedRow = [...table.rows[r]];
    while (paddedRow.length < numCols) paddedRow.push("");
    result = drawRow(paddedRow, y, false, r % 2 === 1);
    if (result.overflow) {
      y = newPage();
      // Re-draw header on new page for continuity
      result = drawRow(paddedHeaders, y, true, false);
      y = result.newY;
      result = drawRow(paddedRow, y, false, false);
    }
    y = result.newY;
  }

  return y + 4;
}

/**
 * Renders mixed content (text + tables) into the PDF.
 * Returns the new Y cursor.
 */
function writeContentWithTables(
  doc: jsPDF,
  markdown: string,
  textX: number,
  textW: number,
  yPos: number,
  lineHeight: number,
  newPageFn: () => number,
): number {
  const segments = parseMarkdownContent(markdown);
  let y = yPos;

  for (const seg of segments) {
    if (seg.type === "table") {
      y = drawTable(doc, seg.table, textX - 2, textW + 2, y, newPageFn);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(FS_BODY);
      y = writeWrappedText(
        doc,
        seg.text,
        textX,
        y,
        textW,
        lineHeight,
        newPageFn,
      );
    }
    y += 2;
  }
  return y;
}

function addPageBackground(doc: jsPDF) {
  doc.setFillColor(248, 250, 252); // slate-50
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");
}

/** Wraps text and returns the new Y cursor, adding pages as needed */
function writeWrappedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  addNewPage: () => number,
): number {
  const lines = doc.splitTextToSize(text, maxWidth) as string[];
  for (const line of lines) {
    if (y > PAGE_H - MARGIN - 6) {
      y = addNewPage();
    }
    doc.text(line, x, y, { maxWidth });
    y += lineHeight;
  }
  return y;
}

/** A closing section listing each source with a working link. */
function writeSourcesSection(
  doc: jsPDF,
  sources: DebateSource[] | undefined,
  newPage: () => number,
  headingColor: [number, number, number],
) {
  const shown = visibleSources(sources ?? []);
  if (shown.length === 0) return;
  let y = newPage();
  doc.setFillColor(...headingColor);
  doc.rect(MARGIN, y, CONTENT_W, 11, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_SECTION);
  doc.text("Sources", MARGIN + 4, y + 7.5);
  y += 18;
  for (const source of shown) {
    const titleLines = doc.splitTextToSize(sanitizeForPDF(source.title), CONTENT_W) as string[];
    if (y + titleLines.length * BODY_LINE_H + 6 > PAGE_H - MARGIN - 12) y = newPage();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(FS_CARD_LABEL + 1);
    doc.setTextColor(29, 78, 216);
    for (const line of titleLines) {
      doc.textWithLink(line, MARGIN, y, { url: source.url });
      y += BODY_LINE_H;
    }
    doc.setFontSize(FS_CARD_LABEL - 1);
    doc.setTextColor(100, 116, 139);
    doc.text(sanitizeForPDF(source.domain), MARGIN, y - 1);
    y += 6;
  }
}

/** Builds and saves the debate PDF, reporting progress for a button or menu item. */
export function useDebatePdf({
  debateTopic,
  councilProgress,
  chaosProgress,
  councilFinal,
  selectedExperts,
  debateMode = "multi",
  sources,
}: DebatePDFButtonProps) {
  const { status, run } = useActionStatus();
  const generate = () => run(async () => {
      if (debateMode === "single") {
        await generateSingleExpertPDF({
          debateTopic,
          councilProgress,
          chaosProgress,
          councilFinal,
          sources,
        });
      } else {
        await generatePDF({
          debateTopic,
          councilProgress,
          chaosProgress,
          councilFinal,
          selectedExperts,
          sources,
        });
      }
  }).catch((err) => console.error("PDF generation failed:", err));
  return { status, generate, label: debateMode === "single" ? "Download analysis PDF" : "Download debate PDF" };
}

export const DebatePDFButton: React.FC<DebatePDFButtonProps> = (props) => {
  const { status, generate, label } = useDebatePdf(props);
  return (
    <ActionButton
      status={status}
      onClick={generate}
      icon={<FileDown size={16} />}
      label={label}
      pendingLabel="Preparing PDF…"
      successLabel="PDF downloaded"
      errorLabel="PDF failed — retry"
      className="px-5 py-2.5 bg-action hover:bg-action-hover active:translate-y-px text-on-solid text-sm font-semibold rounded-md shadow-sm transition-[background-color,transform] duration-150 focus-visible:outline-offset-2"
    />
  );
};

// ─── Core PDF generation ──────────────────────────────────────────────────────
async function generatePDF(params: {
  debateTopic: string;
  councilProgress: DebateStep[];
  chaosProgress: ChaosStep[];
  councilFinal: string | null;
  selectedExperts: string[];
  sources?: DebateSource[];
}) {
  const {
    debateTopic,
    councilProgress,
    chaosProgress,
    councilFinal,
    selectedExperts,
    sources,
  } = params;

  // Rounds are always 1
  const rounds = 1;

  const doc = new jsPDF({ unit: "mm", format: "a4" });

  let currentPage = 1;

  function newPage(): number {
    doc.addPage();
    currentPage++;
    addPageBackground(doc);
    drawPageFooter(doc, currentPage);
    return MARGIN + 10;
  }

  function drawPageFooter(d: jsPDF, page: number) {
    d.setFontSize(FS_FOOTER);
    d.setTextColor(148, 163, 184);
    d.setFont("helvetica", "normal");
    d.text(
      `AI Debate Council  -  Debate Report  -  Page ${page}`,
      PAGE_W / 2,
      PAGE_H - 8,
      { align: "center" },
    );
    // thin footer rule
    d.setDrawColor(226, 232, 240);
    d.setLineWidth(0.3);
    d.line(MARGIN, PAGE_H - 11, PAGE_W - MARGIN, PAGE_H - 11);
  }

  // ── Cover Page ───────────────────────────────────────────────────────────────
  addPageBackground(doc);
  drawPageFooter(doc, 1);

  // blue gradient header band
  doc.setFillColor(37, 99, 235); // blue-600
  doc.rect(0, 0, PAGE_W, 60, "F");
  doc.setFillColor(29, 78, 216); // blue-700 bottom strip
  doc.rect(0, 54, PAGE_W, 8, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_COVER_TITLE);
  doc.text("AI Expert Debate Report", PAGE_W / 2, 28, { align: "center" });

  doc.setFontSize(FS_COVER_SUB);
  doc.setFont("helvetica", "normal");
  doc.text("Powered by Family AI Chat", PAGE_W / 2, 38, { align: "center" });

  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(dateStr, PAGE_W / 2, 46, { align: "center" });

  // Topic card
  let y = 74;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(MARGIN, 66, CONTENT_W, 42, 3, 3, "F");
  doc.setDrawColor(219, 234, 254);
  doc.setLineWidth(0.5);
  doc.roundedRect(MARGIN, 66, CONTENT_W, 42, 3, 3, "S");

  doc.setTextColor(37, 99, 235);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("DEBATE TOPIC", MARGIN + 5, 74);

  doc.setTextColor(30, 41, 59);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  const topicLines = doc.splitTextToSize(
    debateTopic,
    CONTENT_W - 10,
  ) as string[];
  let topicY = 82;
  topicLines.slice(0, 3).forEach((line: string) => {
    doc.text(line, MARGIN + 5, topicY);
    topicY += 7;
  });

  // Stats row
  y = 118;
  const stats = [
    { label: "Experts", value: selectedExperts.length.toString() },
    { label: "Debate Rounds", value: rounds.toString() },
    {
      label: "Total Responses",
      value: councilProgress.length.toString(),
    },
  ];

  stats.forEach((stat, i) => {
    const colW = CONTENT_W / 3;
    const bx = MARGIN + i * colW;
    doc.setFillColor(239, 246, 255);
    doc.roundedRect(bx, y, colW - 3, 22, 2, 2, "F");
    doc.setTextColor(37, 99, 235);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS_STAT_VALUE);
    doc.text(stat.value, bx + colW / 2 - 1.5, y + 12, { align: "center" });
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(FS_STAT_LABEL);
    doc.text(stat.label, bx + colW / 2 - 1.5, y + 19, { align: "center" });
  });

  // Expert chips
  y = 150;
  doc.setTextColor(30, 41, 59);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_CHIP);
  doc.text("Participating Experts", MARGIN, y);
  y += 7;

  let chipX = MARGIN;
  selectedExperts.forEach((expert) => {
    const [r, g, b] = getExpertPalette(expert, selectedExperts).rgb;
    const label = expertLabel(expert);
    const chipW = doc.getStringUnitWidth(label) * FS_CHIP * 0.352 + 10;
    if (chipX + chipW > PAGE_W - MARGIN) {
      chipX = MARGIN;
      y += 9;
    }
    doc.setFillColor(r, g, b);
    doc.roundedRect(chipX, y - 5, chipW, 7, 1.5, 1.5, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS_CHIP);
    doc.text(label, chipX + 5, y);
    chipX += chipW + 3;
  });

  // Table of contents
  y += 18;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 8;
  doc.setTextColor(30, 41, 59);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_TOC);
  doc.text("Contents", MARGIN, y);
  y += 6;

  const tocItems: string[] = ["Initial Responses (All Experts)"];
  for (let r2 = 1; r2 <= rounds; r2++) {
    tocItems.push(`Round ${r2} -- Peer Feedback`);
    tocItems.push(`Round ${r2} -- Chaos Agent Attack`);
    tocItems.push(`Round ${r2} -- Revised Responses`);
  }
  tocItems.push("Facilitator's Final Synthesis");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(FS_TOC);
  tocItems.forEach((item, idx) => {
    doc.setTextColor(71, 85, 105);
    doc.text(`${idx + 1}.  ${item}`, MARGIN + 3, y);
    y += 6.5;
  });

  // ── Helper to draw a section heading ────────────────────────────────────────
  function drawSectionHeading(title: string, yPos: number): number {
    doc.setFillColor(37, 99, 235);
    doc.rect(MARGIN, yPos, CONTENT_W, 11, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS_SECTION);
    doc.text(title, MARGIN + 4, yPos + 7.5);
    return yPos + 11 + 4;
  }

  // ── Helper: draw one expert block (multi-page safe) ─────────────────────────
  function drawExpertBlock(
    expert: string,
    stepLabel: string,
    content: string,
    yPos: number,
  ): number {
    const [r, g, b] = getExpertPalette(expert, selectedExperts).rgb;
    const blockTitle = expertLabel(expert) + " Expert";

    const PAGE_BOTTOM = PAGE_H - MARGIN - 12;
    const HEADER_H = CARD_HEADER_H;

    // Ensure there's room for at least the header on this page
    if (yPos + HEADER_H + BODY_LINE_H > PAGE_BOTTOM) {
      yPos = newPage();
    }

    // Draw header bar
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.6);
    doc.roundedRect(MARGIN, yPos, CONTENT_W, HEADER_H - 4, 2, 2, "FD");

    // Left colour bar
    doc.setFillColor(r, g, b);
    doc.roundedRect(MARGIN, yPos, 3.5, HEADER_H - 4, 1, 1, "F");

    // Header text
    doc.setTextColor(r, g, b);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS_CARD_HEADER);
    doc.text(blockTitle, CARD_TEXT_X, yPos + 9);

    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(FS_CARD_LABEL);
    doc.text(stepLabel, CARD_TEXT_RIGHT, yPos + 9, { align: "right" });

    // Divider
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.2);
    doc.line(CARD_TEXT_X - 1, yPos + 13, CARD_TEXT_RIGHT, yPos + 13);

    yPos += HEADER_H;

    // Body — render with table support
    yPos = writeContentWithTables(
      doc,
      content,
      CARD_TEXT_X,
      CARD_TEXT_W,
      yPos,
      BODY_LINE_H,
      newPage,
    );

    return yPos + 4;
  }

  // ── Build ordered sections ────────────────────────────────────────────────────
  // Section 1: Initial responses
  y = newPage();
  y = drawSectionHeading("1.  Initial Responses — All Experts", y);
  y += 2;

  for (const expert of selectedExperts) {
    const step = councilProgress.find(
      (s) => s.expert === expert && s.step === "initial",
    );
    const content = step?.answer ?? "_No response recorded._";
    y = drawExpertBlock(expert, "Initial Response", content, y);
    y += 4;
  }

  // Sections 2…N: rounds
  let sectionNum = 2;
  for (let roundNum = 1; roundNum <= rounds; roundNum++) {
    // Feedback
    y = newPage();
    y = drawSectionHeading(
      `${sectionNum}.  Round ${roundNum} -- Peer Feedback`,
      y,
    );
    sectionNum++;
    y += 2;

    for (const expert of selectedExperts) {
      const step = councilProgress.find(
        (s) =>
          s.expert === expert && s.step === "feedback" && s.round === roundNum,
      );
      const content = step?.feedback ?? "_No feedback recorded._";
      y = drawExpertBlock(expert, `Feedback - Round ${roundNum}`, content, y);
      y += 4;
    }

    // Chaos Agent Attack
    y = newPage();
    y = drawSectionHeading(
      `${sectionNum}.  Round ${roundNum} -- Chaos Agent Attack`,
      y,
    );
    sectionNum++;
    y += 2;

    const chaosRound = chaosProgress.find((c) => c.round === roundNum);
    const chaosContentMd =
      chaosRound?.chaos || "No Chaos Agent attack recorded for this round.";

    const CHAOS_TEXT_X = MARGIN + 7;
    const CHAOS_TEXT_W = PAGE_W - MARGIN - 4 - CHAOS_TEXT_X - WRAP_SAFETY;
    const PAGE_BOTTOM = PAGE_H - MARGIN - 12;

    if (y + CARD_HEADER_H + BODY_LINE_H > PAGE_BOTTOM) {
      y = newPage();
    }

    // Red card header
    doc.setFillColor(255, 241, 242); // red-50
    doc.setDrawColor(248, 113, 113); // red-400
    doc.setLineWidth(0.7);
    doc.roundedRect(MARGIN, y, CONTENT_W, CARD_HEADER_H - 4, 3, 3, "FD");

    // Red left bar
    doc.setFillColor(239, 68, 68); // red-500
    doc.roundedRect(MARGIN, y, 3.5, CARD_HEADER_H - 4, 1, 1, "F");

    doc.setTextColor(185, 28, 28); // red-700
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS_CHAOS_HEADER);
    doc.text(`Chaos Agent Attack - Round ${roundNum}`, CHAOS_TEXT_X, y + 9);

    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(FS_CARD_LABEL);
    doc.text(
      "Adversarial - Finds fallacies, edge cases & hidden assumptions",
      PAGE_W - MARGIN - 4,
      y + 9,
      { align: "right" },
    );

    doc.setDrawColor(254, 202, 202); // red-200
    doc.setLineWidth(0.2);
    doc.line(CHAOS_TEXT_X - 1, y + 13, PAGE_W - MARGIN - 4, y + 13);

    y += CARD_HEADER_H;

    // Body — render with table support
    y = writeContentWithTables(
      doc,
      chaosContentMd,
      CHAOS_TEXT_X,
      CHAOS_TEXT_W,
      y,
      BODY_LINE_H,
      newPage,
    );

    // Revisions
    y = newPage();
    y = drawSectionHeading(
      `${sectionNum}.  Round ${roundNum} -- Revised Responses`,
      y,
    );
    sectionNum++;
    y += 2;

    for (const expert of selectedExperts) {
      const step = councilProgress.find(
        (s) =>
          s.expert === expert && s.step === "revision" && s.round === roundNum,
      );
      const content = step?.revised ?? "_No revision recorded._";
      y = drawExpertBlock(expert, `Revision - Round ${roundNum}`, content, y);
      y += 4;
    }
  }

  // Final synthesis
  y = newPage();
  y = drawSectionHeading(`${sectionNum}.  Facilitator's Final Synthesis`, y);
  y += 2;

  const finalContent2 = councilFinal || "No final synthesis was recorded.";

  const FINAL_HEADER_H2 = CARD_HEADER_H + 2;
  const FINAL_TEXT_X2 = MARGIN + 5;
  const FINAL_TEXT_W2 = PAGE_W - MARGIN - 4 - FINAL_TEXT_X2 - WRAP_SAFETY;
  const FINAL_PAGE_BOTTOM = PAGE_H - MARGIN - 12;

  if (y + FINAL_HEADER_H2 + BODY_LINE_H > FINAL_PAGE_BOTTOM) {
    y = newPage();
  }

  // Gold accent card header
  doc.setFillColor(254, 252, 232); // yellow-50
  doc.setDrawColor(250, 204, 21); // yellow-400
  doc.setLineWidth(0.7);
  doc.roundedRect(MARGIN, y, CONTENT_W, CARD_HEADER_H - 4, 3, 3, "FD");

  doc.setTextColor(161, 98, 7); // yellow-700
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_FINAL_HEADER);
  doc.text("Facilitator's Final Answer", FINAL_TEXT_X2, y + 9);

  doc.setDrawColor(253, 230, 138);
  doc.setLineWidth(0.2);
  doc.line(FINAL_TEXT_X2 - 1, y + 13, PAGE_W - MARGIN - 4, y + 13);

  y += CARD_HEADER_H;

  // Body — render with table support
  y = writeContentWithTables(
    doc,
    finalContent2,
    FINAL_TEXT_X2,
    FINAL_TEXT_W2,
    y,
    BODY_LINE_H,
    newPage,
  );

  writeSourcesSection(doc, sources, newPage, [37, 99, 235]);

  // Save — Safari iOS ignores the `download` attribute on blob URLs, so we
  // detect it and fall back to opening the blob in a new tab instead.
  const safeTopic = debateTopic.slice(0, 40).replace(/[^a-z0-9 ]/gi, "_");
  const fileName = `debate_${safeTopic}_${Date.now()}.pdf`;

  const blob = doc.output("blob");
  const blobUrl = URL.createObjectURL(blob);

  // iOS Safari detection: it doesn't honour <a download> on blob: URLs
  const isSafariIOS =
    /iP(hone|ad|od)/i.test(navigator.userAgent) &&
    /WebKit/i.test(navigator.userAgent) &&
    !/CriOS|FxiOS|OPiOS|mercury/i.test(navigator.userAgent);

  if (isSafariIOS) {
    // Open in new tab — Safari will show a share sheet with a "Save to Files" option
    window.open(blobUrl, "_blank");
  } else {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // Release the object URL after a short delay to allow the download to start
  setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
}

// ─── Single Expert / Deep Analysis PDF generation ─────────────────────────────
// Phase definitions matching the single-expert pipeline
const SINGLE_PHASES: {
  key: string;
  expert: string;
  step: string;
  label: string;
  emoji: string;
  color: [number, number, number];
  headerBg: [number, number, number];
  borderColor: [number, number, number];
}[] = [
  {
    key: "frame",
    expert: "framer",
    step: "frame",
    label: "Epistemic Framing",
    emoji: "",
    color: [109, 40, 217], // violet-700
    headerBg: [245, 243, 255], // violet-50
    borderColor: [196, 181, 253], // violet-300
  },
  {
    key: "research",
    expert: "researcher",
    step: "research_gather",
    label: "Research",
    emoji: "",
    color: [29, 78, 216], // blue-700
    headerBg: [239, 246, 255], // blue-50
    borderColor: [147, 197, 253], // blue-300
  },
  {
    key: "initial",
    expert: "analyst",
    step: "initial",
    label: "Expert Analysis",
    emoji: "",
    color: [67, 56, 202], // indigo-700
    headerBg: [238, 242, 255], // indigo-50
    borderColor: [165, 180, 252], // indigo-300
  },
  {
    key: "chaos",
    expert: "__chaos__",
    step: "__chaos__",
    label: "Chaos Challenge",
    emoji: "",
    color: [185, 28, 28], // red-700
    headerBg: [254, 242, 242], // red-50
    borderColor: [252, 165, 165], // red-300
  },
  {
    key: "fact_check",
    expert: "fact_checker",
    step: "fact_check",
    label: "Fact Check",
    emoji: "",
    color: [4, 120, 87], // emerald-700
    headerBg: [236, 253, 245], // emerald-50
    borderColor: [110, 231, 183], // emerald-300
  },
];

async function generateSingleExpertPDF(params: {
  debateTopic: string;
  councilProgress: DebateStep[];
  chaosProgress: ChaosStep[];
  councilFinal: string | null;
  sources?: DebateSource[];
}) {
  const { debateTopic, councilProgress, chaosProgress, councilFinal, sources } = params;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let currentPage = 1;

  function newPage(): number {
    doc.addPage();
    currentPage++;
    addPageBackground(doc);
    drawFooter(doc, currentPage);
    return MARGIN + 10;
  }

  function drawFooter(d: jsPDF, page: number) {
    d.setFontSize(FS_FOOTER);
    d.setTextColor(148, 163, 184);
    d.setFont("helvetica", "normal");
    d.text(
      `AI Deep Analysis  -  Report  -  Page ${page}`,
      PAGE_W / 2,
      PAGE_H - 8,
      { align: "center" },
    );
    d.setDrawColor(226, 232, 240);
    d.setLineWidth(0.3);
    d.line(MARGIN, PAGE_H - 11, PAGE_W - MARGIN, PAGE_H - 11);
  }

  // ── Cover Page ──────────────────────────────────────────────────────────────
  addPageBackground(doc);
  drawFooter(doc, 1);

  // Gradient header band
  doc.setFillColor(22, 163, 74); // green-600
  doc.rect(0, 0, PAGE_W, 60, "F");
  doc.setFillColor(21, 128, 61); // green-700
  doc.rect(0, 54, PAGE_W, 8, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_COVER_TITLE);
  doc.text("AI Deep Analysis Report", PAGE_W / 2, 28, { align: "center" });

  doc.setFontSize(FS_COVER_SUB);
  doc.setFont("helvetica", "normal");
  doc.text("Powered by Family AI Chat", PAGE_W / 2, 38, { align: "center" });

  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(dateStr, PAGE_W / 2, 46, { align: "center" });

  // Topic card
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(MARGIN, 66, CONTENT_W, 42, 3, 3, "F");
  doc.setDrawColor(187, 247, 208); // green-200
  doc.setLineWidth(0.5);
  doc.roundedRect(MARGIN, 66, CONTENT_W, 42, 3, 3, "S");

  doc.setTextColor(22, 163, 74);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("ANALYSIS QUESTION", MARGIN + 5, 74);

  doc.setTextColor(30, 41, 59);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  const topicLines = doc.splitTextToSize(
    debateTopic,
    CONTENT_W - 10,
  ) as string[];
  let topicY = 82;
  topicLines.slice(0, 3).forEach((line: string) => {
    doc.text(line, MARGIN + 5, topicY);
    topicY += 7;
  });

  // Pipeline overview
  let y = 118;
  const phasesPresent = SINGLE_PHASES.filter((ph) => {
    if (ph.key === "chaos") return chaosProgress.length > 0;
    return councilProgress.some(
      (s) => s.expert === ph.expert && s.step === ph.step,
    );
  });

  doc.setTextColor(30, 41, 59);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_CHIP);
  doc.text("Analysis Pipeline", MARGIN, y);
  y += 7;

  let chipX = MARGIN;
  const allPhaseLabels = [
    "Frame",
    "Research",
    "Expert",
    "Challenge",
    "Fact-Check",
    "Revise",
  ];
  for (const label of allPhaseLabels) {
    const chipW = doc.getStringUnitWidth(label) * FS_CHIP * 0.352 + 10;
    if (chipX + chipW > PAGE_W - MARGIN) {
      chipX = MARGIN;
      y += 9;
    }
    doc.setFillColor(22, 163, 74);
    doc.roundedRect(chipX, y - 5, chipW, 7, 1.5, 1.5, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS_CHIP);
    doc.text(label, chipX + 5, y);
    chipX += chipW + 3;
  }

  // Table of contents
  y += 18;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 8;
  doc.setTextColor(30, 41, 59);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_TOC);
  doc.text("Contents", MARGIN, y);
  y += 6;

  const tocItems = ["Final Answer", ...phasesPresent.map((ph) => ph.label)];

  doc.setFont("helvetica", "normal");
  doc.setFontSize(FS_TOC);
  tocItems.forEach((item, idx) => {
    doc.setTextColor(71, 85, 105);
    doc.text(`${idx + 1}.  ${item}`, MARGIN + 3, y);
    y += 6.5;
  });

  // ── Section 1: Final Revised Answer (at top, most important) ─────────────
  y = newPage();

  // Section heading
  doc.setFillColor(22, 163, 74); // green-600
  doc.rect(MARGIN, y, CONTENT_W, 11, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_SECTION);
  doc.text("1.  Final Answer", MARGIN + 4, y + 7.5);
  y += 15;

  // Find revision content
  const revisionEntry = councilProgress.find(
    (s) => s.expert === "analyst" && s.step === "revision",
  );
  const finalContent =
    councilFinal ||
    revisionEntry?.revised ||
    revisionEntry?.answer ||
    "_No final answer recorded._";

  // Green-accented card for the final answer
  const FINAL_TEXT_XPOS = MARGIN + 5;
  const FINAL_TEXT_WPOS = PAGE_W - MARGIN - 4 - FINAL_TEXT_XPOS - WRAP_SAFETY;
  const PAGE_BOTTOM = PAGE_H - MARGIN - 12;

  // Card header
  if (y + CARD_HEADER_H + BODY_LINE_H > PAGE_BOTTOM) {
    y = newPage();
  }

  // Draw header for the card
  doc.setFillColor(236, 253, 245); // emerald-50
  doc.setDrawColor(34, 197, 94); // green-500
  doc.setLineWidth(0.7);
  doc.roundedRect(MARGIN, y, CONTENT_W, CARD_HEADER_H - 4, 3, 3, "FD");

  doc.setTextColor(21, 128, 61);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(FS_FINAL_HEADER);
  doc.text("Final Answer", FINAL_TEXT_XPOS, y + 9);

  doc.setDrawColor(187, 247, 208);
  doc.setLineWidth(0.2);
  doc.line(FINAL_TEXT_XPOS - 1, y + 13, PAGE_W - MARGIN - 4, y + 13);

  y += CARD_HEADER_H;

  // Write content with table support
  y = writeContentWithTables(
    doc,
    finalContent,
    FINAL_TEXT_XPOS,
    FINAL_TEXT_WPOS,
    y,
    BODY_LINE_H,
    newPage,
  );

  y += 6;

  // ── Pipeline phases (Frame, Research, Expert, Chaos, Fact-Check) ──────────
  let sectionNum = 2;

  for (const phase of phasesPresent) {
    y = newPage();

    // Section heading
    doc.setFillColor(...phase.color);
    doc.rect(MARGIN, y, CONTENT_W, 11, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS_SECTION);
    doc.text(`${sectionNum}.  ${phase.label}`, MARGIN + 4, y + 7.5);
    y += 15;
    sectionNum++;

    // Get content
    let phaseContent = "";
    if (phase.key === "chaos") {
      const chaosEntry = chaosProgress.find((c) => c.round === 1);
      phaseContent = chaosEntry?.chaos || "_No chaos challenge recorded._";
    } else {
      const entry = councilProgress.find(
        (s) => s.expert === phase.expert && s.step === phase.step,
      );
      phaseContent =
        entry?.answer ||
        entry?.feedback ||
        entry?.revised ||
        `_No ${phase.label.toLowerCase()} recorded._`;
    }

    // Phase card
    const PHASE_TEXT_X = MARGIN + 7;
    const PHASE_TEXT_W = PAGE_W - MARGIN - 4 - PHASE_TEXT_X - WRAP_SAFETY;

    if (y + CARD_HEADER_H + BODY_LINE_H > PAGE_BOTTOM) {
      y = newPage();
    }

    // Card header
    doc.setFillColor(...phase.headerBg);
    doc.setDrawColor(...phase.borderColor);
    doc.setLineWidth(0.6);
    doc.roundedRect(MARGIN, y, CONTENT_W, CARD_HEADER_H - 4, 3, 3, "FD");

    // Left colour bar
    doc.setFillColor(...phase.color);
    doc.roundedRect(MARGIN, y, 3.5, CARD_HEADER_H - 4, 1, 1, "F");

    doc.setTextColor(...phase.color);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(FS_CARD_HEADER);
    doc.text(phase.label, PHASE_TEXT_X, y + 9);

    doc.setDrawColor(...phase.borderColor);
    doc.setLineWidth(0.2);
    doc.line(PHASE_TEXT_X - 1, y + 13, PAGE_W - MARGIN - 4, y + 13);

    y += CARD_HEADER_H;

    // Write content with table support
    y = writeContentWithTables(
      doc,
      phaseContent,
      PHASE_TEXT_X,
      PHASE_TEXT_W,
      y,
      BODY_LINE_H,
      newPage,
    );

    y += 4;
  }

  writeSourcesSection(doc, sources, newPage, [22, 163, 74]);

  // Save
  const safeTopic = debateTopic.slice(0, 40).replace(/[^a-z0-9 ]/gi, "_");
  const fileName = `analysis_${safeTopic}_${Date.now()}.pdf`;

  const blob = doc.output("blob");
  const blobUrl = URL.createObjectURL(blob);

  const isSafariIOS =
    /iP(hone|ad|od)/i.test(navigator.userAgent) &&
    /WebKit/i.test(navigator.userAgent) &&
    !/CriOS|FxiOS|OPiOS|mercury/i.test(navigator.userAgent);

  if (isSafariIOS) {
    window.open(blobUrl, "_blank");
  } else {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
}

export default DebatePDFButton;
